// SEO utilities and structured data for UnityLove
import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { trackEvent } from '../../api/analytics';
import { stashSeoReferrer } from '../../lib/seoReferrer';

// Inline helper: stash referrer + track click in one shot.
const trackCtaClick = (cta, label, surface) => {
  const path = typeof window !== 'undefined' ? window.location.pathname : '';
  stashSeoReferrer(path);
  trackEvent('seo_page_cta_click', { cta, label, surface, path });
};

const PUBLIC_URL = (process.env.REACT_APP_PUBLIC_APP_URL || 'https://JoinUnityLove.com').replace(/\/$/, '');

// Brand constants
export const BRAND = {
  name: 'UnityLove',
  tagline: 'Dating for Intentional Connection',
  url: PUBLIC_URL,
  appStoreUrl: 'https://apps.apple.com/app/unitylove',
  playStoreUrl: 'https://play.google.com/store/apps/details?id=com.unitylove',
  logo: `${PUBLIC_URL}/logo512.png`,
  email: 'hello@joinunitylove.com',
};

// JSON-LD: Organization Schema
export const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: BRAND.name,
  url: BRAND.url,
  logo: BRAND.logo,
  sameAs: [
    'https://instagram.com/unitylove',
    'https://twitter.com/unitylove',
    'https://facebook.com/unitylove',
  ],
  contactPoint: {
    '@type': 'ContactPoint',
    email: BRAND.email,
    contactType: 'customer service',
  },
};

// JSON-LD: Mobile Application Schema
export const mobileAppSchema = {
  '@context': 'https://schema.org',
  '@type': 'MobileApplication',
  name: 'UnityLove – Black & Poly Dating App',
  operatingSystem: 'iOS, Android',
  applicationCategory: 'DatingApplication',
  offers: {
    '@type': 'Offer',
    price: '0',
    priceCurrency: 'USD',
  },
  // NOTE: aggregateRating removed — Google requires real, publicly verifiable
  // reviews. Re-enable only when reviews/ratings are actually collected.
  description: 'UnityLove is a Black poly dating app built for ethical non-monogamy, conscious relationships, and intentional Black communities.',
};

// JSON-LD: Website Schema
export const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: BRAND.name,
  url: BRAND.url,
  potentialAction: {
    '@type': 'SearchAction',
    target: `${BRAND.url}/search?q={search_term_string}`,
    'query-input': 'required name=search_term_string',
  },
};

// SEO Head Component
//
// Strategy:
//   - <Helmet> handles <title> + <meta> reliably (v2.0.5 has good support there)
//   - Canonical <link> + JSON-LD <script> are injected directly into <head>
//     via useEffect, because react-helmet-async v2.0.5 silently drops these
//     even when using dangerouslySetInnerHTML. Direct DOM injection is what
//     Googlebot reads in production anyway (Googlebot now executes JS).
//   - Each instance tags the elements with data-seo-managed="<canonical-path>"
//     so the cleanup function removes the right ones on route change.
export function SEOHead({ 
  title, 
  description, 
  canonical, 
  schemas = [],
  ogImage = '/og-image.png',
}) {
  const fullTitle = title.includes('UnityLove') ? title : `${title} | UnityLove`;
  const canonicalUrl = `${BRAND.url}${canonical}`;
  const ogImageUrl = `${BRAND.url}${ogImage}`;

  // Direct DOM injection of <link rel="canonical"> + JSON-LD <script> tags.
  // Cleaned up when the page unmounts so each route owns its own SEO state.
  useEffect(() => {
    const head = document.head;
    const added = [];
    const tag = `unitylove-seo-${canonical}`;

    // 1. Remove any prior <link rel="canonical"> (static or otherwise) so ours wins.
    const priorCanonicals = head.querySelectorAll('link[rel="canonical"]');
    priorCanonicals.forEach((el) => el.parentNode?.removeChild(el));

    // 2. Inject our canonical link.
    const linkEl = document.createElement('link');
    linkEl.setAttribute('rel', 'canonical');
    linkEl.setAttribute('href', canonicalUrl);
    linkEl.setAttribute('data-seo-managed', tag);
    head.appendChild(linkEl);
    added.push(linkEl);

    // 3. Inject each JSON-LD schema as a <script type="application/ld+json">.
    schemas.forEach((schema) => {
      try {
        const s = document.createElement('script');
        s.type = 'application/ld+json';
        s.text = JSON.stringify(schema);
        s.setAttribute('data-seo-managed', tag);
        head.appendChild(s);
        added.push(s);
      } catch {
        // never break the page if a schema is malformed
      }
    });

    // 4. Cleanup on unmount / route change.
    return () => {
      added.forEach((el) => {
        if (el.parentNode) el.parentNode.removeChild(el);
      });
    };
    // canonical is the stable identity for this set of tags
  }, [canonical, canonicalUrl, JSON.stringify(schemas)]);

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={ogImageUrl} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={BRAND.name} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImageUrl} />
    </Helmet>
  );
}

// Internal link component for SEO
export function InternalLink({ to, children, className = '' }) {
  return (
    <a 
      href={to} 
      className={`text-rose-600 hover:text-rose-700 underline font-medium ${className}`}
    >
      {children}
    </a>
  );
}

// CTA Button for app download
export function AppDownloadCTA({ variant = 'primary' }) {
  const baseStyles = "inline-flex items-center justify-center px-6 py-3 rounded-xl font-semibold transition-all";
  const variants = {
    primary: "bg-slate-900 text-white hover:bg-slate-800",
    secondary: "bg-rose-600 text-white hover:bg-rose-700",
    outline: "border-2 border-slate-900 text-slate-900 hover:bg-slate-50",
  };
  
  return (
    <div className="flex flex-col sm:flex-row gap-4 items-center justify-center my-8">
      <a 
        href={BRAND.appStoreUrl}
        className={`${baseStyles} ${variants[variant]}`}
        target="_blank"
        rel="noopener noreferrer"
      >
        Download on App Store
      </a>
      <a 
        href={BRAND.playStoreUrl}
        className={`${baseStyles} ${variants[variant]}`}
        target="_blank"
        rel="noopener noreferrer"
      >
        Get it on Google Play
      </a>
    </div>
  );
}

// JSON-LD: FAQ Schema builder
// Pass an array of { q, a } objects; returns a schema.org FAQPage doc
// that you can spread into the SEOHead `schemas` prop.
export function buildFaqSchema(faqs = []) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: a,
      },
    })),
  };
}

// JSON-LD: BreadcrumbList builder
// items: [{ name, url }] - url is relative (e.g. "/guides")
export function buildBreadcrumbSchema(items = []) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.name,
      item: `${BRAND.url}${item.url}`,
    })),
  };
}

// Hero with brand gradient + headline + sub + dual CTAs
export function GradientHero({
  eyebrow,
  headline,
  sub,
  body,
  primaryCta = { label: 'Create Your Profile', to: '/auth?mode=signup' },
  secondaryCta = null, // { label, to } or null
}) {
  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-rose-50 via-white to-amber-50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
      <div className="absolute inset-0 bg-[radial-gradient(60%_60%_at_50%_0%,rgba(244,114,182,0.15),transparent_70%)] pointer-events-none" />
      <div className="relative max-w-4xl mx-auto px-4 py-20 sm:py-24 text-center">
        {eyebrow && (
          <span className="inline-block mb-4 px-3 py-1 rounded-full bg-rose-100 dark:bg-rose-900/40 text-rose-700 dark:text-rose-300 text-xs font-semibold tracking-wide uppercase">
            {eyebrow}
          </span>
        )}
        <h1 className="font-playfair text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 dark:text-slate-50 mb-5">
          {headline}
        </h1>
        {sub && (
          <p className="text-lg sm:text-xl text-slate-700 dark:text-slate-200 mb-3 max-w-2xl mx-auto">
            {sub}
          </p>
        )}
        {body && (
          <p className="text-base text-slate-600 dark:text-slate-300 mb-8 max-w-2xl mx-auto">
            {body}
          </p>
        )}
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <a
            href={primaryCta.to}
            onClick={() => trackCtaClick('primary', primaryCta.label, 'hero')}
            className="inline-flex items-center justify-center px-7 py-3.5 bg-rose-600 text-white rounded-full font-semibold hover:bg-rose-700 active:scale-95 transition-all shadow-sm"
          >
            {primaryCta.label}
          </a>
          {secondaryCta && (
            <a
              href={secondaryCta.to}
              onClick={() => trackCtaClick('secondary', secondaryCta.label, 'hero')}
              className="inline-flex items-center justify-center px-7 py-3.5 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 border border-slate-200 dark:border-slate-700 rounded-full font-semibold hover:bg-slate-50 dark:hover:bg-slate-800 transition-all"
            >
              {secondaryCta.label}
            </a>
          )}
        </div>
      </div>
    </section>
  );
}

// Large feature card for trust pillars / topic blocks
export function FeatureCard({ icon, title, body, accent = 'rose' }) {
  const accentMap = {
    rose: 'bg-rose-50 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300',
    emerald: 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300',
    violet: 'bg-violet-50 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300',
    amber: 'bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300',
    sky: 'bg-sky-50 dark:bg-sky-900/30 text-sky-700 dark:text-sky-300',
  };
  return (
    <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-6 hover:shadow-md transition-shadow">
      <div className={`inline-flex h-11 w-11 items-center justify-center rounded-full text-xl ${accentMap[accent] || accentMap.rose}`}>
        {icon}
      </div>
      <h3 className="mt-4 text-lg font-semibold text-slate-900 dark:text-slate-50">{title}</h3>
      <p className="mt-2 text-slate-700 dark:text-slate-300 leading-relaxed">{body}</p>
    </div>
  );
}

// Numbered step row for "How It Works" / process timelines
export function StepCard({ n, title, body }) {
  return (
    <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-6">
      <div className="flex items-start gap-4">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-rose-600 text-white font-semibold">
          {n}
        </div>
        <div>
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-50">{title}</h3>
          <p className="mt-1.5 text-slate-700 dark:text-slate-300 leading-relaxed">{body}</p>
        </div>
      </div>
    </div>
  );
}

// Accordion FAQ (visible to humans; pairs with FAQ JSON-LD for Google rich snippets)
export function FaqList({ items = [] }) {
  return (
    <div className="space-y-3" data-testid="faq-list">
      {items.map(({ q, a }, i) => (
        <details
          key={i}
          className="group rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-5 open:shadow-sm"
        >
          <summary className="cursor-pointer list-none flex items-start justify-between gap-4">
            <span className="text-base sm:text-lg font-semibold text-slate-900 dark:text-slate-50">
              {q}
            </span>
            <span
              aria-hidden="true"
              className="mt-1 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-rose-100 dark:bg-rose-900/40 text-rose-700 dark:text-rose-300 group-open:rotate-45 transition-transform font-bold"
            >
              +
            </span>
          </summary>
          <div className="mt-3 text-slate-700 dark:text-slate-300 leading-relaxed">{a}</div>
        </details>
      ))}
    </div>
  );
}

// Internal link cluster — render at the bottom of every page in this cluster
// for both SEO link equity and human discoverability.
const RELATED_GUIDES = [
  { to: '/how-to-find-a-sister-wife', label: 'How To Find A Sister Wife' },
  { to: '/sister-wife-dating-app', label: 'Sister Wife Dating App' },
  { to: '/black-sister-wife-dating', label: 'Black Sister Wife Dating' },
  { to: '/sister-wives-and-poly-family-building', label: 'Sister Wives & Poly Family Building' },
  { to: '/poly-dating-for-couples', label: 'Poly Dating For Couples' },
  { to: '/ethical-polyamory-for-beginners', label: 'Ethical Polyamory For Beginners' },
  { to: '/safe-poly-dating', label: 'Safe Poly Dating Guide' },
  { to: '/building-a-polygamous-family', label: 'Building A Polygamous Family' },
  { to: '/poly-family-planning', label: 'Poly Family Planning' },
];

export function RelatedGuides({ currentPath }) {
  const items = RELATED_GUIDES.filter((g) => g.to !== currentPath);
  return (
    <section className="bg-slate-50 dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800 py-14 px-4">
      <div className="max-w-4xl mx-auto">
        <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-slate-50 mb-2 font-playfair">
          Continue Reading
        </h2>
        <p className="text-slate-600 dark:text-slate-300 mb-6">
          Explore more guides about intentional relationships, poly family-building, and community.
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          {items.map(({ to, label }) => (
            <a
              key={to}
              href={to}
              className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-4 py-3 text-slate-800 dark:text-slate-200 hover:border-rose-400 hover:text-rose-700 dark:hover:text-rose-300 transition-colors flex items-center justify-between group"
            >
              <span className="font-medium">{label}</span>
              <span
                aria-hidden
                className="text-slate-400 dark:text-slate-500 group-hover:text-rose-600 dark:group-hover:text-rose-400 group-hover:translate-x-0.5 transition-all"
              >
                →
              </span>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

// Final CTA card — every page ends with this
export function BottomCTA({
  headline = 'Ready to build meaningful connections?',
  body = 'Create your profile on UnityLove and connect with singles, couples, and families who share your relationship goals.',
}) {
  return (
    <section className="bg-gradient-to-br from-rose-50 via-white to-amber-50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950 py-16 px-4">
      <div className="max-w-3xl mx-auto text-center rounded-3xl border border-rose-100 dark:border-rose-900/40 bg-white dark:bg-slate-900 p-8 sm:p-10 shadow-sm">
        <h2 className="font-playfair text-3xl sm:text-4xl font-bold text-slate-900 dark:text-slate-50">
          {headline}
        </h2>
        <p className="mt-3 text-slate-700 dark:text-slate-300 max-w-xl mx-auto">{body}</p>
        <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
          <a
            href="/auth?mode=signup"
            onClick={() => trackCtaClick('primary', 'Create Your Profile', 'bottom')}
            className="inline-flex items-center justify-center px-7 py-3.5 bg-rose-600 text-white rounded-full font-semibold hover:bg-rose-700 active:scale-95 transition-all shadow-sm"
          >
            Create Your Profile
          </a>
          <a
            href="/guides"
            onClick={() => trackCtaClick('secondary', 'Browse All Guides', 'bottom')}
            className="inline-flex items-center justify-center px-7 py-3.5 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 border border-slate-200 dark:border-slate-700 rounded-full font-semibold hover:bg-slate-50 dark:hover:bg-slate-700 transition-all"
          >
            Browse All Guides
          </a>
        </div>
      </div>
    </section>
  );
}

// JSON-LD: Article schema for reports / long-form analyses.
// Pass:
//   headline, description, urlPath, datePublishedISO, dateModifiedISO (optional)
// The author/publisher are baked to UnityLove.
export function buildArticleSchema({
  headline,
  description,
  urlPath,
  datePublishedISO,
  dateModifiedISO,
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline,
    description,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${BRAND.url}${urlPath}`,
    },
    author: {
      '@type': 'Organization',
      name: BRAND.name,
      url: BRAND.url,
    },
    publisher: {
      '@type': 'Organization',
      name: BRAND.name,
      url: BRAND.url,
      logo: {
        '@type': 'ImageObject',
        url: `${BRAND.url}/logo512.png`,
      },
    },
    datePublished: datePublishedISO,
    ...(dateModifiedISO ? { dateModified: dateModifiedISO } : {}),
  };
}

// Reusable "About This Report" honesty block for authority pages.
// Used in /state-of-* style reports so readers/journalists know how the
// observations were assembled. This is critical for backlink credibility —
// it signals integrity rather than fabricated statistics.
export function AboutThisReport({
  published = 'February 2026',
  body = 'This report draws on observations across the UnityLove community, public research on dating and relationship trends, and conversations with members across the African diaspora. Where specific statistics are cited, public sources are referenced. Where directional language is used, it reflects qualitative patterns observed across the platform — not survey data.',
}) {
  return (
    <aside
      className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 p-5 sm:p-6 my-8"
      data-testid="about-this-report"
    >
      <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-2">
        About This Report
      </h3>
      <p className="text-slate-700 dark:text-slate-300 text-sm leading-relaxed">{body}</p>
      <p className="text-slate-500 dark:text-slate-400 text-xs mt-3">Published {published} · UnityLove Research</p>
    </aside>
  );
}

// Visual "Report" hero with eyebrow, dateline, headline, and a dek paragraph.
// Different look from GradientHero so authority pages feel like reports,
// not landing pages.
export function ReportHero({
  eyebrow = 'UnityLove Research',
  dateline,
  headline,
  dek,
}) {
  return (
    <section className="border-b border-slate-200 dark:border-slate-800">
      <div className="max-w-3xl mx-auto px-4 py-12 sm:py-16">
        <div className="text-xs font-semibold uppercase tracking-[0.18em] text-rose-600 dark:text-rose-400 mb-3">
          {eyebrow}
        </div>
        {dateline && (
          <div className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-5">
            {dateline}
          </div>
        )}
        <h1 className="font-playfair text-4xl sm:text-5xl font-bold text-slate-900 dark:text-slate-50 leading-tight tracking-tight">
          {headline}
        </h1>
        {dek && (
          <p className="mt-5 text-lg sm:text-xl text-slate-700 dark:text-slate-200 leading-relaxed max-w-2xl">
            {dek}
          </p>
        )}
      </div>
    </section>
  );
}

// Numbered section heading for report pages.
export function ReportSection({ n, title, children }) {
  return (
    <section className="mb-12">
      <div className="flex items-baseline gap-3 mb-4">
        <span className="font-playfair text-2xl font-bold text-rose-600 dark:text-rose-400">
          {String(n).padStart(2, '0')}
        </span>
        <h2 className="font-playfair text-2xl sm:text-3xl font-bold text-slate-900 dark:text-slate-50">
          {title}
        </h2>
      </div>
      <div className="text-slate-700 dark:text-slate-300 leading-relaxed space-y-4">{children}</div>
    </section>
  );
}

// Pull-quote callout for report pages.
export function PullQuote({ children }) {
  return (
    <blockquote className="my-8 border-l-4 border-rose-500 pl-5 py-1 italic text-slate-800 dark:text-slate-200 text-lg sm:text-xl leading-relaxed">
      &ldquo;{children}&rdquo;
    </blockquote>
  );
}

export default SEOHead;
